<template>
    <purchase-contract-section>
        <template #title>
            <div class="flex w-full flex-col">
                <div
                    v-if="linkedCourseNote !== undefined"
                    class="mb-4 flex w-full whitespace-pre-wrap border-4 bg-gray-100 p-6 text-lg font-medium"
                >
                    {{ linkedCourseNote }}
                </div>
                <div class="flex w-full items-center justify-between">
                    <div>
                        <div class="flex flex-col gap-1 md:flex-row">
                            <div>
                                {{ t('purchaseContract.selectParticipants') }}
                            </div>
                            <div class="font-normal">
                                <span class="hidden lg:inline">- </span
                                >{{ activeFacility.shortName }}
                            </div>
                        </div>
                    </div>

                    <div>
                        <prime-button
                            :class="[
                                contractData !== undefined && !isActiveSection
                                    ? ''
                                    : 'invisible'
                            ]"
                            @click="emit('editSection')"
                        >
                            <div class="flex items-center gap-2">
                                <i class="pi pi-pencil" />
                                <span class="lg:hidden">{{ t('edit') }}</span>
                                <span class="hidden lg:inline">{{
                                    t('purchaseContract.changeOrder')
                                }}</span>
                            </div>
                        </prime-button>
                    </div>
                </div>
            </div>
        </template>

        <div
            v-if="!isActiveSection && contractData !== undefined"
            class="flex flex-wrap gap-x-16 gap-y-6 py-4"
        >
            <div v-if="contractData.course !== null">
                <label class="font-semibold">{{ t('waitlist.course') }}</label>
                <div>
                    {{ contractData.course.publicTitle }}
                </div>
            </div>

            <div v-if="contractData.startSession !== null">
                <label class="font-semibold">{{
                    t('purchaseContract.startingSession')
                }}</label>
                <div>
                    <client-only>
                        {{
                            formatRange(
                                parseLocal(
                                    contractData.startSession.startDateLocal
                                ),
                                parseLocal(
                                    contractData.startSession.endDateLocal
                                )
                            )
                        }}
                    </client-only>
                </div>
            </div>

            <div v-if="contractData.nextBillDate !== null">
                <label class="font-semibold">{{
                    t('contracts.nextBillDate')
                }}</label>
                <div>
                    <client-only>
                        {{
                            formatTimelessDate(
                                parseTimeless(contractData.nextBillDate)
                            )
                        }}
                    </client-only>
                </div>
            </div>

            <div>
                <label class="font-semibold">{{
                    t('purchaseContract.participants')
                }}</label>
                <div
                    v-if="contractData.enrollmentSpecifications.length === 0"
                    class="text-gray-400"
                >
                    &mdash;
                </div>
                <ul class="list-disc pl-6">
                    <li
                        v-for="enrollmentSpecification in contractData.enrollmentSpecifications"
                        :key="enrollmentSpecification.enrollmentType.id"
                    >
                        {{ enrollmentSpecification.count }}
                        {{ enrollmentSpecification.enrollmentType.singular }}
                    </li>
                </ul>
            </div>

            <div v-if="contractData.promotions.length > 0">
                <label class="font-semibold">{{
                    t('purchaseContract.promotionalCodes')
                }}</label>
                <div class="flex flex-wrap gap-2">
                    <prime-message
                        v-for="promotion in contractData.promotions"
                        :key="promotion.code"
                        severity="success"
                        :closable="false"
                        icon="pi pi-tag"
                        class="!my-0"
                    >
                        <span class="font-semibold">{{ promotion.code }}</span>
                        <template
                            v-if="
                                promotion.promotionCode?.promotion
                                    .translatedDescription ?? ''
                            "
                        >
                            <br />
                            {{
                                promotion.promotionCode?.promotion
                                    .translatedDescription ?? ''
                            }}
                        </template>
                    </prime-message>
                </div>
            </div>
        </div>

        <div
            v-if="isActiveSection"
            class="space-y-10"
        >
            <div class="flex flex-col items-start gap-8 md:flex-row">
                <div
                    class="flex flex-col justify-between self-stretch md:w-1/4"
                >
                    <div class="space-y-4">
                        <enrollment-type-selectors
                            v-model:enrollment-input="enrollmentInput"
                        />
                    </div>

                    <promotional-code
                        v-model="appliedPromotionalCodes"
                        :plan-id="planId"
                        :start-date="selectedSession?.startDateLocal ?? null"
                    />
                </div>

                <div class="space-y-4 md:w-3/4">
                    <div>
                        {{ t('purchaseContract.chooseAvailableCourses') }}
                    </div>

                    <skeleton v-if="courseOptions === undefined" />
                    <div v-else-if="courseOptions.length === 0">
                        {{ t('purchaseContract.noCoursesAvailableForPlan') }}
                    </div>
                    <template v-else>
                        <recurring-course-radio-button
                            v-for="course in courseOptions"
                            :key="course.id"
                            :course="course"
                            :selected="course.id === selectedCourseId"
                            :availability-threshold="
                                planData.presentation?.availabilityThreshold ??
                                null
                            "
                            :waitlist-enabled="
                                planData.bookingLimit.waitlistEnabled
                            "
                            @select="handleCourseSelection(course)"
                        />
                    </template>
                </div>
            </div>

            <div>
                <div class="font-semibold uppercase tracking-wider">
                    {{ t('purchaseContract.selectStartingSession') }}
                </div>

                <div class="mt-2 flex flex-wrap items-center gap-4 text-sm">
                    <div class="flex items-center gap-2">
                        <calendar-availability-marker status="available" />
                        {{ t('purchaseContract.available') }}
                    </div>

                    <div class="flex items-center gap-2">
                        <div
                            class="bg-stripes bg-stripes-white relative h-5 w-5 bg-gray-200"
                        ></div>
                        {{ t('purchaseContract.today') }}
                    </div>
                </div>

                <div class="flex flex-col gap-8 md:flex-row">
                    <div class="-mx-4">
                        <prime-calendar
                            v-model="selectedDate"
                            inline
                            select-other-months
                            :pt="{
                                day: ({ context }) => ({
                                    'aria-label': getCalendarDayLabel(
                                        context.date
                                    ),
                                    class: [
                                        'SelectedCourseDay children:w-[3.2rem] children:h-[3.2rem] children:!rounded-none children:!bg-transparent',
                                        context.today
                                            ? 'bg-gray-200 bg-stripes bg-stripes-white'
                                            : ''
                                    ]
                                }),
                                dayLabel: ({ context }) => {
                                    // Name the focusable control and expose its selection as button state.
                                    return {
                                        role: 'button',
                                        'aria-pressed': context.selected,
                                        'aria-selected': null,
                                        'aria-label': getCalendarDayLabel(
                                            context.date
                                        )
                                    }
                                },
                                panel: ({ props }) => ({
                                    class: [
                                        'bg-white dark:bg-gray-900',
                                        'min-w-[350px]',
                                        {
                                            'inline-block overflow-x-auto p-2':
                                                props.inline
                                        }
                                    ]
                                })
                            }"
                            @month-change="handleMonthChange"
                            @year-change="handleYearChange"
                        >
                            <template #date="{ date }">
                                <select-session-day
                                    :dates-to-sessions="selectedCourseSessions"
                                    :date="date"
                                />
                            </template>
                        </prime-calendar>
                    </div>

                    <div class="w-full space-y-8">
                        <template v-if="selectedCourse !== undefined">
                            <div v-if="selectedDateSessions === null">
                                {{
                                    t(
                                        'purchaseContract.selectedCourseUnavailable'
                                    )
                                }}
                            </div>
                            <div v-else-if="selectedDateSessions.length === 0">
                                <template
                                    v-if="
                                        selectedCourse?.firstFutureSession ===
                                        null
                                    "
                                >
                                    {{
                                        t(
                                            'purchaseContract.noSessionsAvailableForCourse'
                                        )
                                    }}
                                </template>
                                <template v-else>
                                    {{
                                        t(
                                            'purchaseContract.noSessionsAvailableForDate'
                                        )
                                    }}
                                </template>
                            </div>
                            <div
                                v-for="session in selectedDateSessions"
                                v-else
                                :key="session.data.id"
                            >
                                <session-display
                                    :session="session.data"
                                    :selected="
                                        session.data.id === selectedSessionId
                                    "
                                    :disabled="
                                        waitlistMode === WaitlistMode.Available
                                    "
                                    @select="
                                        handleSessionSelection(session.data)
                                    "
                                />
                            </div>
                        </template>

                        <div class="flex-1">
                            <template v-if="showCourseErrorOrPricing">
                                <app-message
                                    v-if="courseIsUnavailableMessage !== null"
                                    theme="danger"
                                >
                                    {{ courseIsUnavailableMessage }}
                                </app-message>

                                <div
                                    v-else-if="
                                        courseWaitlistBookingMessage !== null
                                    "
                                >
                                    <app-message theme="danger">
                                        <div
                                            class="flex flex-1 flex-col items-center justify-between gap-2 sm:flex-row"
                                        >
                                            {{
                                                courseWaitlistBookingMessage.message
                                            }}
                                            <prime-button
                                                v-if="
                                                    courseWaitlistBookingMessage.type ===
                                                    'waitlisted'
                                                "
                                                :label="t('signIn')"
                                                class="shrink-0 whitespace-nowrap"
                                                @click="handleSignIn"
                                            />
                                        </div>
                                    </app-message>
                                </div>

                                <app-message
                                    v-else-if="
                                        tooManyEnrollmentsMessage !== null
                                    "
                                    theme="danger"
                                >
                                    {{ tooManyEnrollmentsMessage }}
                                </app-message>

                                <app-message
                                    v-else-if="
                                        bookingLimitsErrorMessage !== null &&
                                        continueAttempts > 0
                                    "
                                    theme="danger"
                                >
                                    {{ bookingLimitsErrorMessage }}
                                </app-message>

                                <div
                                    v-else-if="
                                        sessionIsUnavailableMessage !== null
                                    "
                                >
                                    <div class="space-y-2">
                                        <app-message theme="danger">
                                            {{
                                                sessionIsUnavailableMessage.message
                                            }}
                                        </app-message>

                                        <div
                                            v-for="option in sessionIsUnavailableMessage.data"
                                            :key="option.session.sessionId"
                                        >
                                            {{
                                                t(
                                                    'purchaseContract.customerCanStartOn',
                                                    {
                                                        customerName:
                                                            option.customerName
                                                    }
                                                )
                                            }}
                                            <prime-button
                                                link
                                                class="!p-0"
                                                @click="
                                                    handleJumpToSession(
                                                        option.session
                                                            .startDate,
                                                        option.session.sessionId
                                                    )
                                                "
                                            >
                                                {{
                                                    formatTimelessDate(
                                                        parseTimeless(
                                                            option.session
                                                                .startDate
                                                        )
                                                    )
                                                }}
                                            </prime-button>
                                        </div>
                                    </div>
                                </div>

                                <template v-else>
                                    <dues-pricing-display
                                        :dues-pricing="duesPricing"
                                        :billing-interval="
                                            planData.terms.billingInterval
                                        "
                                        :billing-cycle-string="
                                            planData.terms.billingCycleString
                                        "
                                        :next-bill-date="nextBillDate"
                                    />

                                    <preview-contract-pricing-table
                                        :is-loading="isFetching"
                                        :cart="cart"
                                        :payment-due-mode="paymentDueMode"
                                        :deposit-amount="depositAmount"
                                    />
                                </template>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex justify-end">
                <template
                    v-if="
                        waitlistMode === WaitlistMode.Closed ||
                        waitlistMode === WaitlistMode.Full ||
                        waitlistMode === WaitlistMode.Available
                    "
                >
                    <prime-button
                        :outlined="
                            courseWaitlistBookingMessage !== null &&
                            courseWaitlistBookingMessage.type === 'waitlisted'
                                ? true
                                : undefined
                        "
                        :disabled="waitlistMode !== WaitlistMode.Available"
                        :loading="isFetchingWaitlistData"
                        :label="t('waitlist.join')"
                        @click="handleJoinWaitlist"
                    />
                </template>

                <template v-else-if="selectedCourse !== undefined">
                    <prime-button
                        v-if="contractData === undefined"
                        :label="t('continue')"
                        :disabled="disableContinueButton"
                        :loading="isMutatingContract"
                        @click="handleContinue"
                    />

                    <prime-button
                        v-else
                        :label="t('continue')"
                        :disabled="disableContinueButton"
                        :loading="isMutatingContract"
                        @click="handleSaveChanges"
                    />
                </template>
            </div>
        </div>
    </purchase-contract-section>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const SelectRecurringProgramStartCourseFragment = graphql(
    /** GraphQL */ `
        fragment SelectRecurringProgramStartCourseFragment on Course {
            ...RecurringCourseRadioButtonCourseFragment
            id
            boundsStartLocal
            boundsEndLocal
            bookingStatus
            capacityResult(filter: $capacityResultFilter) {
                enrollments
                contracts
                overlappedBookedSessions
                isUnavailable
                isWaitlistFull
            }
            bookingPeriodAvailable(customerId: null, bookingDate: null)
            bookingStatusAvailable(filter: $bookingStatusFilter) {
                customerName
                session {
                    sessionId
                    startDate
                }
                overrideCapacityConstraints
            }
            firstFutureSession {
                startDateLocal
            }
        }
    `
)

export const SelectRecurringProgramStartSessionFragment = graphql(
    /** GraphQL */ `
        fragment SelectRecurringProgramStartSessionFragment on Session {
            ...SessionDisplaySessionFragment
            startDateLocal
            id
            sessionFacilityHash
        }
    `
)

export const SelectRecurringProgramStartPlanFragment = graphql(/** GraphQL */ `
    fragment SelectRecurringProgramStartPlanFragment on Plan {
        id
        terms {
            enrollmentRanges {
                enrollmentType {
                    id
                    singular
                    plural
                }
                minQuantity
                maxQuantity
            }
            allowGuestCheckout
            billingInterval
            billingType
            billingCycleString(language: $language)
        }
        bookingLimit(input: $input) {
            minimumEnrollments
            maximumEnrollments
            waitlistEnabled
        }
        presentation(input: $input, language: $language) {
            availabilityThreshold
            showCustomEvents
        }
    }
`)

export const SelectRecurringProgramStartContractFragment = graphql(
    /** GraphQL */ `
        fragment SelectRecurringProgramStartContractFragment on PlanContract {
            id
            updatedAtUtc
            draftExpiresAtUtc
            startDateLocal
            nextBillDate
            facility {
                shortName
            }
            enrollmentSpecifications {
                enrollmentType {
                    id
                    singular
                    plural
                }
                count
            }
            course {
                id
                ...SelectRecurringProgramStartCourseFragment
            }
            totalEnrollmentCount
            startSession {
                id
                startDateLocal
                endDateLocal
            }
            course {
                publicTitle
            }
            promotions {
                id
                code
                fullCode
                source
                canRemove
                promotionCode {
                    promotion {
                        translatedDescription(language: $language)
                    }
                }
            }
        }
    `
)
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import PrimeButton from 'primevue/button'
import PurchaseContractSection from '~/components/PurchaseContract/PurchaseContractSection.vue'
import {
    useActiveFacility,
    useRpQuery,
    useShowLoginModal,
    useStorefront,
    useViewer
} from '#imports'
import RecurringCourseRadioButton from '~/components/PurchaseContract/Program/RecurringCourseRadioButton.vue'
import {
    formatRange,
    formatTimelessDate,
    now,
    parseLocal,
    parseTimeless,
    toTimelessIso,
    toUtcIsoString
} from '~/composables/support/localization'
import type {
    BookingStatusAvailableFilter,
    CapacityResultFilter,
    ChangeContractInput,
    CreateContractInput,
    SelectRecurringProgramStartCourseFragmentFragment,
    SelectRecurringProgramStartSessionFragmentFragment
} from '~/resources/graphql/graphql'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import PrimeCalendar, {
    type CalendarDateContext,
    type CalendarMonthChangeEvent,
    type CalendarYearChangeEvent
} from 'primevue/calendar'
import EnrollmentTypeSelectors from '~/components/PurchaseContract/EnrollmentTypeSelectors.vue'
import PreviewContractPricingTable from '~/components/PurchaseContract/PreviewContractPricingTable.vue'
import PrimeMessage from 'primevue/message'
import { usePreviewSessionContractQuery } from '~/components/PurchaseContract/use-preview-session-contract-query'
import {
    endOfMonth,
    endOfWeek,
    max,
    startOfDay,
    startOfWeek as startOfWeekDate
} from 'date-fns'
import { useQuery } from '@tanstack/vue-query'
import SelectSessionDay from '~/components/PurchaseContract/Program/SelectSessionDay.vue'
import CalendarAvailabilityMarker from './CalendarAvailabilityMarker.vue'
import { useRoute } from '#app'
import { navigateTo } from '#app/composables/router'
import { useLastDeletedContractId } from '~/composables/use-cart'
import Skeleton from 'primevue/skeleton'
import DuesPricingDisplay from '~/components/PurchaseContract/DuesPricingDisplay.vue'
import SessionDisplay from '~/components/PurchaseContract/Program/SessionDisplay.vue'
import { zonedTimeToUtc } from 'date-fns-tz'
import {
    type AppliedPromotionalCode,
    getForInput,
    hasSameAppliedPromotionalCodes
} from '~/components/PurchaseContract/promotional-code'
import PromotionalCode from '~/components/PurchaseContract/PromotionalCode.vue'
import { useJoinWaitlistModal } from '~/components/Waitlist/use-join-waitlist-modal'
import AppMessage from '~/components/AppMessage.vue'
import { useTooManyEnrollmentsMessage } from '~/components/PurchaseContract/Program/helpers/use-too-many-enrollments-message'
import { useCourseIsUnavailableMessage } from '~/components/PurchaseContract/Program/helpers/use-course-is-unavailable-message'
import { useBookingLimitsErrorMessage } from '~/components/PurchaseContract/Program/helpers/use-booking-limits-error-message'
import {
    useWaitlistMode,
    WaitlistMode
} from '~/components/PurchaseContract/Program/helpers/use-waitlist-mode'
import { useCourseWaitlistBookingMessage } from '~/components/PurchaseContract/Program/helpers/use-course-waitlist-booking-message'
import { useI18n } from 'vue-i18n'
import { useLocalization } from '~/composables/use-localization'

const props = defineProps<{
    plan: FragmentType<typeof SelectRecurringProgramStartPlanFragment>
    contract:
        | FragmentType<typeof SelectRecurringProgramStartContractFragment>
        | undefined
    isActiveSection: boolean
    isMutatingContract: boolean
}>()

const emit = defineEmits<{
    (e: 'createContract', payload: CreateContractInput): void
    (e: 'changeContract', payload: ChangeContractInput): void
    (e: 'editSection'): void
}>()

const planData = computed(() =>
    getFragmentData(SelectRecurringProgramStartPlanFragment, props.plan)
)

const contractData = computed(() =>
    props.contract === undefined
        ? props.contract
        : getFragmentData(
              SelectRecurringProgramStartContractFragment,
              props.contract
          )
)

const viewer = useViewer()
const showLoginModal = useShowLoginModal()
const activeFacility = useActiveFacility()
const { t } = useI18n({ useScope: 'global' })
const { startOfWeek: localizedStartOfWeek, dateFormatter } = useLocalization()
const planId = computed(() => planData.value.id)
const facilityId = computed(() => activeFacility.value.id)
const appliedPromotionalCodes = ref<AppliedPromotionalCode[]>([])

const route = useRoute()
const selectedDate = computed<Date>({
    get() {
        const routeDate = route.query.date
        if (
            routeDate === undefined ||
            routeDate === null ||
            routeDate === '' ||
            Array.isArray(routeDate)
        ) {
            return startOfDay(now(activeFacility.value.timezone))
        }

        const parsed = parseTimeless(routeDate)
        if (isNaN(parsed.valueOf())) {
            return startOfDay(now(activeFacility.value.timezone))
        }

        return parsed
    },
    set(newValue) {
        const sessions = selectedCourseSessions.value.get(
            toTimelessIso(newValue)
        )
        let firstSession = undefined
        if (sessions !== undefined && sessions.length > 0) {
            firstSession = sessions[0].data.id
        }
        navigateTo(
            {
                ...route,
                query: {
                    ...route.query,
                    course: selectedCourseId.value,
                    date: toTimelessIso(newValue),
                    session: firstSession
                }
            },
            { replace: true }
        )
    }
})

const viewStartDate = ref()
const viewEndDate = ref()
const calendarWeekStartsOn = computed(() => {
    // date-fns accepts only 0-6 for weekStartsOn; fall back to Sunday if the
    // organization setting ever arrives outside that range.
    if (localizedStartOfWeek.value < 0 || localizedStartOfWeek.value > 6) {
        return 0
    }

    return localizedStartOfWeek.value as 0 | 1 | 2 | 3 | 4 | 5 | 6
})
// month is January => 1, not 0
function calculateViewDates(month: number, year: number) {
    const firstDayOfMonth = parseTimeless(
        year + '-' + String(month).padStart(2, '0') + '-01'
    )
    viewStartDate.value = startOfWeekDate(firstDayOfMonth, {
        weekStartsOn: calendarWeekStartsOn.value
    })
    viewEndDate.value = endOfWeek(endOfMonth(firstDayOfMonth), {
        weekStartsOn: calendarWeekStartsOn.value
    })
}
watch(
    [selectedDate, calendarWeekStartsOn],
    ([selectedViewDate]) => {
        calculateViewDates(
            // Zero based, but calendar event is 1 based
            selectedViewDate.getMonth() + 1,
            selectedViewDate.getFullYear()
        )
    },
    { immediate: true }
)

function handleMonthChange(event: CalendarMonthChangeEvent) {
    // On month change, we set selectedDate to the first of the month
    selectedDate.value = parseTimeless(
        event.year + '-' + String(event.month).padStart(2, '0') + '-01'
    )
    calculateViewDates(event.month, event.year)
}

function handleYearChange(event: CalendarYearChangeEvent) {
    // On month change, we set selectedDate to the first of the month
    selectedDate.value = parseTimeless(
        event.year + '-' + String(event.month).padStart(2, '0') + '-01'
    )
    calculateViewDates(event.month, event.year)
}

const lastDeletedContractId = useLastDeletedContractId()

const capacityResultFilter = computed<CapacityResultFilter>(() => {
    if (contractData.value === undefined) {
        return { ignoreContractId: null }
    }

    // For race condition where deleted contract ID would get used in course query below
    if (lastDeletedContractId.value === contractData.value.id) {
        return { ignoreContractId: null }
    }

    return { ignoreContractId: contractData.value.id }
})

const bookingStatusFilter = computed<BookingStatusAvailableFilter>(() => {
    return {
        customerId: viewer.value !== undefined ? viewer.value.customerId : null
    }
})

const linkedCourseId = computed<string | undefined>(() => {
    const routeLinkedCourseId = route.query.linkedCourse
    if (
        routeLinkedCourseId === undefined ||
        routeLinkedCourseId === null ||
        routeLinkedCourseId === '' ||
        Array.isArray(routeLinkedCourseId)
    ) {
        return undefined
    }

    return routeLinkedCourseId
})

const linkedCourseNote = computed<string | undefined>(() => {
    const routeLinkedCourseNote = route.query.linkedCourseNote
    if (
        routeLinkedCourseNote === undefined ||
        routeLinkedCourseNote === null ||
        routeLinkedCourseNote === '' ||
        Array.isArray(routeLinkedCourseNote)
    ) {
        return undefined
    }

    return routeLinkedCourseNote
})

const sessionStartDateAfter = computed(() =>
    toUtcIsoString(
        zonedTimeToUtc(
            max([viewStartDate.value, now(activeFacility.value.timezone)]),
            activeFacility.value.timezone
        )
    )
)

const sessionStartDateBefore = computed(() =>
    toUtcIsoString(
        zonedTimeToUtc(
            max([viewEndDate.value, now(activeFacility.value.timezone)]),
            activeFacility.value.timezone
        )
    )
)

const courseEndDateAfter = computed(() =>
    toUtcIsoString(
        zonedTimeToUtc(
            now(activeFacility.value.timezone),
            activeFacility.value.timezone
        )
    )
)

const courseFilter = computed(() => {
    if (linkedCourseId.value !== undefined) {
        return {
            facilityIds: [facilityId.value],
            linkedCourseId: linkedCourseId.value
        }
    } else {
        return {
            facilityIds: [facilityId.value],
            endDateAfter: courseEndDateAfter.value,
            showCustom: planData.value.presentation?.showCustomEvents ?? false
        }
    }
})

const sessionFilter = computed(() => {
    return {
        facilityId: facilityId.value,
        startDateAfter: sessionStartDateAfter.value,
        startDateBefore: sessionStartDateBefore.value,
        isCancelled: false
    }
})

const rpQuery = useRpQuery()
const { data } = useQuery({
    queryKey: [
        'SelectRecurringProgramCourseCoursesQuery',
        planId,
        courseFilter,
        capacityResultFilter,
        bookingStatusFilter,
        sessionFilter
    ],
    placeholderData: previousData => previousData,
    queryFn: async () => {
        const result = await rpQuery(
            graphql(`
                query SelectRecurringProgramCourseCoursesQuery(
                    $planId: ID!
                    $courseFilter: CourseFilter!
                    $capacityResultFilter: CapacityResultFilter!
                    $bookingStatusFilter: BookingStatusAvailableFilter!
                    $sessionFilter: SessionFilter
                ) {
                    plan(id: $planId) {
                        courses(filter: $courseFilter, first: 150) {
                            edges {
                                node {
                                    ...SelectRecurringProgramStartCourseFragment
                                    id
                                    publicTitle
                                    sessions(filter: $sessionFilter) {
                                        ...SelectRecurringProgramStartSessionFragment
                                    }
                                }
                            }
                        }
                    }
                }
            `),
            {
                planId: planId.value,
                courseFilter: courseFilter.value,
                capacityResultFilter: capacityResultFilter.value,
                bookingStatusFilter: bookingStatusFilter.value,
                sessionFilter: sessionFilter.value
            }
        )

        if (result.plan === null) {
            throw new Error()
        }

        return result.plan
    }
})

const courseOptions = computed(() => {
    if (data.value === undefined) {
        return undefined
    }

    // Sort courses alphabetically by public title
    let courses = data.value.courses.edges.map(edge => edge.node)
    courses = courses.sort((a, b) => a.publicTitle.localeCompare(b.publicTitle))

    return courses.map(courseNode =>
        getFragmentData(SelectRecurringProgramStartCourseFragment, courseNode)
    )
})

const enrollmentInput = ref(
    planData.value.terms.enrollmentRanges.map(range => {
        return {
            enrollmentType: range.enrollmentType,
            minQuantity: range.minQuantity,
            maxQuantity: range.maxQuantity,
            count: range.minQuantity
        }
    })
)

const enrollmentTypeCounts = computed(() => {
    return enrollmentInput.value
        .filter(element => element.count > 0)
        .map(element => {
            return {
                enrollmentTypeId: element.enrollmentType.id,
                count: element.count
            }
        })
})

const totalEnrollmentCount = computed(() => {
    return enrollmentInput.value.reduce((prev, curr) => prev + curr.count, 0)
})

const tooManyEnrollmentsMessage = useTooManyEnrollmentsMessage(
    totalEnrollmentCount,
    computed(() => planData.value.bookingLimit.waitlistEnabled),
    computed(() => {
        if (selectedCourse.value === undefined) {
            return null
        }

        return {
            bookingStatusAvailable: selectedCourse.value.bookingStatusAvailable,
            enrollments: selectedCourse.value.capacityResult.enrollments,
            isWaitlistFull: selectedCourse.value.capacityResult.isWaitlistFull
        }
    })
)

const courseIsUnavailableMessage = useCourseIsUnavailableMessage(
    computed(() => planData.value.bookingLimit.waitlistEnabled),
    computed(() => {
        if (selectedCourse.value === undefined) {
            return null
        }

        return {
            capacityResult: {
                isWaitlistFull:
                    selectedCourse.value.capacityResult.isWaitlistFull,
                overlappedBookedSessions:
                    selectedCourse.value.capacityResult
                        .overlappedBookedSessions,
                contracts: selectedCourse.value.capacityResult.contracts
            }
        }
    })
)

const bookingLimitsErrorMessage = useBookingLimitsErrorMessage(
    totalEnrollmentCount,
    computed(() => planData.value.bookingLimit.minimumEnrollments),
    computed(() => planData.value.bookingLimit.maximumEnrollments)
)

const courseWaitlistBookingMessage = useCourseWaitlistBookingMessage(
    computed(() => planData.value.bookingLimit.waitlistEnabled),
    computed(() => {
        if (selectedCourse.value === undefined) {
            return null
        }

        return {
            bookingStatus: selectedCourse.value.bookingStatus,
            bookingStatusAvailable: selectedCourse.value.bookingStatusAvailable,
            capacityResult: {
                isWaitlistFull:
                    selectedCourse.value.capacityResult.isWaitlistFull
            }
        }
    })
)

const sessionIsUnavailableMessage = computed(() => {
    // No course selected, no warning - handled by others
    if (selectedCourse.value === undefined) {
        return null
    }

    // Open or closed booking, no warning - handled by others
    if (selectedCourse.value.bookingStatus !== 'WAITLIST') {
        return null
    }

    // No booking status available, no warning - handled by others
    if (selectedCourse.value.bookingStatusAvailable.length === 0) {
        return null
    }

    // No session selected, no warning - handled by others
    const session = selectedSession.value
    if (session === null) {
        return null
    }

    // Not our problem then
    if (totalEnrollmentCount.value === 0) {
        return null
    }

    // Find a valid session
    const validCustomersInSelectedSession =
        selectedCourse.value.bookingStatusAvailable.filter(
            status =>
                status.session === null ||
                status.session.sessionId === session.id
        )

    // No results, send back the warning
    if (validCustomersInSelectedSession.length === 0) {
        return {
            message: t('purchaseContract.registrationStartDateLimited'),
            data: selectedCourse.value.bookingStatusAvailable
        }
    }

    // Now make sure we have enough enrollments excluding the override ones
    const overrideCount = validCustomersInSelectedSession.filter(
        status => status.overrideCapacityConstraints
    ).length

    const spaceAvailable =
        Math.max(selectedCourse.value.capacityResult.enrollments, 0) +
        overrideCount

    // Now make sure we don't have more enrollments than we should
    if (totalEnrollmentCount.value > spaceAvailable) {
        const availability = t(
            'purchaseContract.availableSpaces',
            {
                count: spaceAvailable
            },
            spaceAvailable
        )
        const message = t(
            'purchaseContract.selectedParticipantsAvailability',
            {
                selectedCount: totalEnrollmentCount.value,
                availability
            },
            totalEnrollmentCount.value
        )

        return {
            message,
            data: selectedCourse.value.bookingStatusAvailable
        }
    }

    return null
})

const showCourseErrorOrPricing = computed(() => {
    if (selectedCourse.value === undefined) {
        return false
    }

    if (selectedCourse.value.bookingPeriodAvailable !== 'AVAILABLE') {
        return false
    }

    if (selectedCourse.value.bookingStatus === 'CLOSED') {
        return false
    }

    return true
})

const selectedCourseId = computed<string | undefined>({
    get() {
        const routeCourseId = route.query.course
        if (
            routeCourseId === undefined ||
            routeCourseId === null ||
            routeCourseId === '' ||
            Array.isArray(routeCourseId)
        ) {
            return undefined
        }

        return routeCourseId
    },
    set(newValue) {
        navigateTo(
            {
                ...route,
                query: {
                    ...route.query,
                    course: newValue,
                    session: undefined
                }
            },
            { replace: true }
        )
    }
})

const selectedCourse = computed<
    SelectRecurringProgramStartCourseFragmentFragment | undefined
>(() => {
    if (selectedCourseId.value === undefined || data.value === undefined) {
        return undefined
    }

    const course = data.value.courses.edges.find(
        edge => edge.node.id === selectedCourseId.value
    )

    if (course === undefined) {
        return undefined
    }

    return getFragmentData(
        SelectRecurringProgramStartCourseFragment,
        course.node
    )
})

const selectedSessionId = computed<string | null>({
    get() {
        const routeSessionId = route.query.session
        if (
            routeSessionId === undefined ||
            routeSessionId === null ||
            routeSessionId === '' ||
            Array.isArray(routeSessionId)
        ) {
            return null
        }

        return routeSessionId
    },
    set(newValue) {
        navigateTo(
            {
                ...route,
                query: {
                    ...route.query,
                    session: newValue
                }
            },
            { replace: true }
        )
    }
})

const selectedSession =
    computed<SelectRecurringProgramStartSessionFragmentFragment | null>(() => {
        if (
            selectedSessionId.value === null ||
            selectedDateSessions.value === null ||
            selectedDateSessions.value.length === 0
        ) {
            return null
        }

        const session = selectedDateSessions.value.find(
            session => session.data.id === selectedSessionId.value
        )

        if (session === undefined) {
            return null
        } else {
            return session.data
        }
    })

function handleCourseSelection(
    event: SelectRecurringProgramStartCourseFragmentFragment
) {
    selectedCourseId.value = event.id
}

const selectedCourseSessions = computed(() => {
    // Map from dates to sessions
    const map = new Map<
        string,
        {
            data: SelectRecurringProgramStartSessionFragmentFragment
            bookingStatusAvailable: boolean
        }[]
    >()

    // If there is no selected course return empty map
    if (selectedCourseId.value === undefined || data.value === undefined) {
        return map
    }

    // Get the selected course
    const courseEdge = data.value.courses.edges.find(
        edge => edge.node.id === selectedCourseId.value
    )
    if (courseEdge === undefined) {
        throw new Error()
    }

    const courseData = getFragmentData(
        SelectRecurringProgramStartCourseFragment,
        courseEdge.node
    )

    // For each session of the selected course
    courseEdge.node.sessions.forEach(session => {
        const sessionData = getFragmentData(
            SelectRecurringProgramStartSessionFragment,
            session
        )

        const bookingStatusAvailable = courseData.bookingStatusAvailable.some(
            spot =>
                spot.session !== null &&
                spot.session.sessionId === sessionData.id
        )

        // Add the session to the map
        const key = toTimelessIso(parseLocal(sessionData.startDateLocal))
        const existing = map.get(key)

        if (existing !== undefined) {
            existing.push({ data: sessionData, bookingStatusAvailable })
        } else {
            map.set(key, [
                {
                    data: sessionData,
                    bookingStatusAvailable
                }
            ])
        }
    })

    return map
})

// If the selected course changes, and the currently displayed month has no sessions
// Set the selected date to first future session date
watch(selectedCourseId, async newValue => {
    if (newValue === undefined || data.value === undefined) {
        return
    }

    // Get the selected course data
    const courseEdge = data.value.courses.edges.find(
        edge => edge.node.id === selectedCourseId.value
    )
    if (courseEdge === undefined) {
        throw new Error()
    }
    const selectedCourseData = getFragmentData(
        SelectRecurringProgramStartCourseFragment,
        courseEdge.node
    )

    if (selectedCourseData.firstFutureSession === null) {
        return
    }

    const firstFutureSessionStartDate = parseLocal(
        selectedCourseData.firstFutureSession.startDateLocal
    )

    // If current month has no sessions
    if (selectedCourseSessions.value.size === 0) {
        selectedDate.value = firstFutureSessionStartDate
    }
})

// Match the session-presence marker while giving keyboard focus a full date
// and translated status; dates without session data keep their date-only label.
function getCalendarDayLabel(date: CalendarDateContext): string {
    const calendarDate = new Date(date.year, date.month, date.day)
    const sessions = selectedCourseSessions.value.get(
        toTimelessIso(calendarDate)
    )
    const dateLabel = dateFormatter.value.format(calendarDate)

    return sessions !== undefined && sessions.length > 0
        ? dateLabel + ', ' + t('purchaseContract.available')
        : dateLabel
}

const selectedDateSessions = computed(() => {
    if (selectedCourse.value === undefined) {
        return null
    }

    if (selectedCourse.value.bookingPeriodAvailable !== 'AVAILABLE') {
        return null
    }

    const key = toTimelessIso(selectedDate.value)
    const sessions = selectedCourseSessions.value.get(key)
    if (sessions === undefined) {
        return []
    } else {
        return sessions
    }
})

function handleSessionSelection(
    session: SelectRecurringProgramStartSessionFragmentFragment
) {
    selectedSessionId.value = session.id
}

const selectedSessionStartDate = computed(() => {
    if (selectedSession.value === null) {
        return null
    } else {
        return selectedSession.value.startDateLocal
    }
})

const {
    cart,
    duesPricing,
    promotions,
    nextBillDate,
    paymentDueMode,
    depositAmount,
    isFetching,
    subtotal,
    total
} = usePreviewSessionContractQuery(
    selectedSessionId,
    selectedSessionStartDate,
    enrollmentTypeCounts,
    appliedPromotionalCodes
)

const continueAttempts = ref(0)
const canContinue = computed(() => {
    if (selectedCourse.value === undefined) {
        return false
    }

    if (selectedSession.value === null) {
        return false
    }

    if (tooManyEnrollmentsMessage.value !== null) {
        return false
    }

    if (bookingLimitsErrorMessage.value !== null) {
        return false
    }

    if (sessionIsUnavailableMessage.value !== null) {
        return false
    }

    if (subtotal.value === undefined || total.value === undefined) {
        return false
    }

    return true
})

const storefront = useStorefront()
const allowGuestCheckout = computed<boolean>(() => {
    if (planData.value.terms.billingType === 'RECURRING') {
        return (
            storefront.value.allowRecurringGuestCheckout &&
            planData.value.terms.allowGuestCheckout
        )
    }
    return (
        storefront.value.allowGuestCheckout &&
        planData.value.terms.allowGuestCheckout
    )
})

async function handleContinue() {
    if (!canContinue.value) {
        continueAttempts.value += 1
        return
    }

    let customerId = viewer.value?.customerId
    if (customerId === undefined) {
        customerId = await showLoginModal({
            showGuest: allowGuestCheckout.value
        })
        if (customerId === undefined) {
            return
        }
    }

    if (selectedSession.value === null || selectedCourse.value === undefined) {
        throw new Error()
    }
    emit('createContract', {
        customerId,
        sessionFacilityHash: selectedSession.value.sessionFacilityHash,
        startDate: selectedSession.value.startDateLocal,
        endDate: selectedCourse.value.boundsEndLocal,
        enrollmentTypeCounts: enrollmentTypeCounts.value,
        promotionCodes: getForInput(appliedPromotionalCodes.value),
        subtotal: subtotal.value,
        total: total.value
    })
}

const canSaveChanges = computed(() => {
    if (selectedCourse.value === undefined) {
        return false
    }

    if (selectedSession.value === null) {
        return false
    }

    if (tooManyEnrollmentsMessage.value !== null) {
        return false
    }

    if (bookingLimitsErrorMessage.value !== null) {
        return false
    }

    if (subtotal.value === undefined || total.value === undefined) {
        return false
    }

    return true
})

async function handleSaveChanges() {
    if (!canSaveChanges.value) {
        continueAttempts.value += 1
        return
    }

    if (contractData.value === undefined) {
        // We should not call save changes once we have an ID
        throw new Error()
    }

    if (selectedSession.value === null || selectedCourse.value === undefined) {
        throw new Error()
    }
    emit('changeContract', {
        id: contractData.value.id,
        updatedAt: contractData.value.updatedAtUtc,
        startSessionFacilityHash: selectedSession.value.sessionFacilityHash,
        startDate: selectedSession.value.startDateLocal,
        endDate: selectedCourse.value.boundsEndLocal,
        nextBillDate: nextBillDate.value,
        enrollmentTypeCounts: enrollmentTypeCounts.value,
        promotionCodes: getForInput(appliedPromotionalCodes.value),
        subtotal: subtotal.value,
        total: total.value
    })
}

watch(
    promotions,
    newValue => {
        if (newValue === undefined || newValue === null) {
            return
        }

        const nextPromotions = newValue.map(promotion => {
            return {
                code: promotion.code,
                fullCode: promotion.fullCode,
                source: promotion.source,
                canRemove: promotion.canRemove,
                description:
                    promotion.promotionCode.promotion.translatedDescription ??
                    ''
            }
        })

        if (
            !hasSameAppliedPromotionalCodes(
                appliedPromotionalCodes.value,
                nextPromotions
            )
        ) {
            // Pricing preview and submitted promotions need to stay aligned as
            // session/start-date changes churn the auto-applied subset.
            appliedPromotionalCodes.value = nextPromotions
        }
    },
    { immediate: true }
)

watch(
    contractData,
    newValue => {
        if (newValue === undefined) {
            return
        }

        enrollmentInput.value = enrollmentInput.value.map(input => {
            const contractInput = newValue.enrollmentSpecifications.find(
                spec => spec.enrollmentType.id === input.enrollmentType.id
            )

            if (contractInput === undefined) {
                return { ...input }
            }

            return {
                ...input,
                count: contractInput.count
            }
        })

        // A null course is an orphaned booking, so they just need to pick a new course
        if (newValue.course !== null) {
            const courseData = getFragmentData(
                SelectRecurringProgramStartCourseFragment,
                newValue.course
            )
            const newDate = toTimelessIso(parseLocal(newValue.startDateLocal))
            const newCourseId = courseData.id
            const newSessionId = newValue.startSession?.id
            // If we are not on the correct date/course redirect
            if (
                toTimelessIso(selectedDate.value) !== newDate ||
                newCourseId !== selectedCourseId.value
            ) {
                navigateTo(
                    {
                        ...route,
                        query: {
                            ...route.query,
                            date: newDate,
                            course: newCourseId,
                            session: newSessionId
                        }
                    },
                    { replace: true }
                )
            }
        }

        appliedPromotionalCodes.value = newValue.promotions.map(promotion => ({
            contractPromotionId: promotion.id,
            fullCode: promotion.fullCode,
            code: promotion.code,
            source: promotion.source,
            canRemove: promotion.canRemove,
            description:
                promotion.promotionCode?.promotion.translatedDescription ?? ''
        }))
    },
    { immediate: true }
)

const disableContinueButton = computed(() => {
    return (
        !showCourseErrorOrPricing.value ||
        courseIsUnavailableMessage.value !== null ||
        selectedSessionId.value === null ||
        sessionIsUnavailableMessage.value !== null
    )
})

const waitlistMode = useWaitlistMode(
    computed(() => {
        return {
            waitlistEnabled: planData.value.bookingLimit.waitlistEnabled,
            hoursBeforeCourseWaitlistCloses: null // Not enabled for recurring
        }
    }),
    computed(() => {
        if (selectedCourse.value === undefined) {
            return null
        }

        return {
            boundsStartLocal: selectedCourse.value.boundsStartLocal,
            bookingStatus: selectedCourse.value.bookingStatus,
            bookingStatusAvailable: selectedCourse.value.bookingStatusAvailable,
            capacityResult: {
                isWaitlistFull:
                    selectedCourse.value.capacityResult.isWaitlistFull,
                overlappedBookedSessions:
                    selectedCourse.value.capacityResult
                        .overlappedBookedSessions,
                contracts: selectedCourse.value.capacityResult.contracts,
                enrollments: selectedCourse.value.capacityResult.enrollments
            }
        }
    })
)

const { isFetching: isFetchingWaitlistData, showJoinWaitlistModal } =
    useJoinWaitlistModal()
async function handleJoinWaitlist() {
    if (selectedCourse.value === undefined) {
        throw new Error()
    }

    let customerId = viewer.value?.customerId
    if (customerId === undefined) {
        customerId = await showLoginModal({
            showGuest: false
        })
        if (customerId === undefined) {
            return
        }
    }

    await showJoinWaitlistModal({
        courseId: selectedCourse.value.id,
        facilityId: activeFacility.value.id
    })
}

async function handleSignIn() {
    let customerId = viewer.value?.customerId
    if (customerId === undefined) {
        customerId = await showLoginModal({
            showGuest: false
        })
        if (customerId === undefined) {
            return
        }
    }

    // Booking status filter should force a refresh of the query
}

function handleJumpToSession(date: string, sessionId: string) {
    navigateTo(
        {
            ...route,
            query: {
                ...route.query,
                date: date,
                session: sessionId
            }
        },
        { replace: true }
    )
}
</script>

<style lang="postcss">
.SelectedCourseDay > span[data-p-highlight='true'] {
    /* Keep selection neutral so the brand color belongs to availability markers. */
    @apply border border-gray-400;
}
</style>
